// src/pages/Exams/generate/client.ts
import API from '../../../utils/constant';
import type {
  Blueprint, JobRow, NationalExamAggregate, NextExamPrediction, QuestionDraft, SaveResponse,
  RegenVariant, Template, JobHistoryRow,
} from './types';

const BASE = '/api/school/exam-gen';

/** One form of an exam set. Everything after `exam_name` is a newer additive field. */
export interface ExamSetForm {
  form_label: string; exam_id: number; exam_name: string;
  status: 'Active' | 'Draft' | null; section_names: string[] | null;
  question_count: number | null; time_given: number | null; due_at: string | null;
  took: number | null; assigned: number | null; avg_score: number | null;
}

/** A row of the exam-sets list. Detail is owner-only, so link a row only when `is_mine`. */
export interface ExamSetListRow {
  id: number; name: string; created_at: string;
  subject_id: number | null; subject_name: string; grade_id: number | null; grade_name: string;
  forms: number; creator_name: string; is_mine: boolean; generation_job_id: number | null;
}

// The backend (api/school_panel) serializes jobs with a nested `progress`
// object and an `exam_set` FK; the frontend works against a flat JobRow.
// Normalize at this boundary so every component/hook keeps the clean shape.
function normalizeJob(raw: any): JobRow {
  const p = raw?.progress ?? {};
  return {
    ...raw,
    progress_total: p.total ?? raw?.progress_total ?? 0,
    progress_completed: p.completed ?? raw?.progress_completed ?? 0,
    progress_failed: p.failed ?? raw?.progress_failed ?? 0,
    exam_set_id: raw?.exam_set ?? raw?.exam_set_id ?? null,
    output_questions: raw?.output_questions ?? [],
    failed_chapters: raw?.failed_chapters ?? [],
    blueprint: raw?.blueprint ?? {},
    style_profile: raw?.style_profile ?? {},
  } as JobRow;
}

function normalizeHistoryRow(raw: any): JobHistoryRow {
  const p = raw?.progress ?? {};
  return {
    id: raw.id,
    phase: raw.phase,
    progress_total: p.total ?? 0,
    progress_completed: p.completed ?? 0,
    progress_failed: p.failed ?? 0,
    created_at: raw.created_at,
    updated_at: raw.updated_at,
    completed_at: raw.completed_at ?? null,
    name: raw.name ?? '',
    subject_name: raw.subject_name ?? '',
    duration_min: raw.duration_min ?? null,
    num_forms: raw.num_forms ?? 1,
    exam_set: raw.exam_set ?? null,
    grade_name: raw.grade_name ?? '',
    total_questions: raw.total_questions ?? null,
    started_at: raw.started_at ?? null,
  };
}

export const ExamGenApi = {
  // Backend responds { id, phase }; expose it as { job_id, phase } for the hook.
  createJob: (blueprint: Blueprint, cycleId?: number | null) =>
    API.post<{ id: number; phase: string }>(
      `${BASE}/jobs/`, cycleId ? { blueprint, cycle_id: cycleId } : { blueprint })
      .then(r => ({ job_id: r.data.id, phase: r.data.phase })),

  getJob: (jobId: number) =>
    API.get(`${BASE}/jobs/${jobId}/`).then(r => normalizeJob(r.data)),

  regenerateQuestion: (jobId: number, questionIndex: number, variant: RegenVariant) =>
    API.post<QuestionDraft>(
      `${BASE}/jobs/${jobId}/regenerate-question/`,
      { question_index: questionIndex, variant }).then(r => r.data),

  retryChapter: (jobId: number, formLabel: string, chapterId: number) =>
    API.post<{ replaced: number }>(
      `${BASE}/jobs/${jobId}/retry-chapter/`,
      { form_label: formLabel, chapter_id: chapterId }).then(r => r.data),

  listHistory: (params: { status?: string; limit?: number } = {}) =>
    API.get<{ jobs: any[] }>(`${BASE}/jobs/`, { params })
      .then(r => (r.data.jobs ?? []).map(normalizeHistoryRow)),

  listTemplates: () =>
    API.get<{ templates: Template[] }>(`${BASE}/templates/`)
      .then(r => r.data.templates ?? []),

  createTemplate: (name: string, blueprint: Blueprint) =>
    API.post<Template>(`${BASE}/templates/`, { name, blueprint }).then(r => r.data),

  updateTemplate: (id: number, patch: { name?: string; blueprint?: Blueprint }) =>
    API.patch<Template>(`${BASE}/templates/${id}/`, patch).then(r => r.data),

  deleteTemplate: (id: number) =>
    API.delete(`${BASE}/templates/${id}/`).then(() => undefined),

  shareTemplate: (id: number) =>
    API.post<{ is_shared: boolean }>(`${BASE}/templates/${id}/share/`).then(r => r.data),

  listJobs: (params: { status?: string; limit?: number } = {}) =>
    API.get<{ jobs: any[] }>(`${BASE}/jobs/`, { params })
      .then(r => ({ jobs: (r.data.jobs ?? []).map(normalizeJob) })),

  cancelJob: (jobId: number) =>
    API.post<{ cancel_requested: boolean }>(
      `${BASE}/jobs/${jobId}/cancel/`).then(r => r.data),

  saveJob: (jobId: number, body: {
    name: string;
    expected_revision: number;
    forms: Array<{ form_label: string; questions: QuestionDraft[] }>;
  }) =>
    API.post<SaveResponse>(`${BASE}/jobs/${jobId}/save/`, body).then(r => r.data),

  predictNextExam: (neId: number, subjectId: number, total: number) =>
    API.get<NextExamPrediction>(`${BASE}/predict/${neId}/${subjectId}/`, { params: { total } }).then(r => r.data),

  styleProfile: (neId: number, subjectId: number) =>
    API.get<Record<string, unknown>>(
      `${BASE}/style-profile/${neId}/${subjectId}/`).then(r => r.data),

  // Backend NationalAggregateAPIView returns { total, chapters: [{id, name, avg}],
  // exams_count }. Map it to the NationalExamAggregate shape QuickModeForm reads.
  aggregateDistribution: (neId: number, subjectId: number) =>
    API.get<any>(`/api/exam/national/${neId}/aggregate/${subjectId}/`)
      .then(r => {
        const d = r.data ?? {};
        const chapters: Array<{ id: number; avg: number }> = d.chapters ?? [];
        return {
          insufficient_data: (d.exams_count ?? 0) === 0,
          chapter_counts: chapters.map(c => ({
            chapter_id: c.id, count: Math.max(0, Math.round(c.avg ?? 0)),
          })),
          total_questions: d.total ?? 0,
        } as NationalExamAggregate;
      }),

  // Backend ExamSetSerializer returns { id, name, created_at, blueprint_snapshot,
  // forms: [{form_label, exam_id, ...}] }. Map `forms` -> `memberships`. The per-form
  // fields and `generation_job_id` / `creator_name` are newer additive fields: absent
  // on older backends, so every one is optional.
  examSetDetail: (examSetId: number) =>
    API.get<any>(`${BASE}/exam-sets/${examSetId}/`).then(r => {
      const d = r.data ?? {};
      const forms: any[] = d.forms ?? d.memberships ?? [];
      return {
        id: d.id as number,
        name: d.name as string,
        created_at: d.created_at as string,
        generation_job_id: (d.generation_job_id ?? null) as number | null,
        creator_name: (d.creator_name ?? null) as string | null,
        // Pass-through of the stored blueprint; older sets may lack any field.
        blueprint: (d.blueprint_snapshot ?? null) as Partial<Blueprint> | null,
        memberships: forms.map((f): ExamSetForm => ({
          form_label: f.form_label,
          exam_id: f.exam_id,
          exam_name: f.exam_name ?? '',
          status: f.status ?? null,
          section_names: Array.isArray(f.section_names) ? f.section_names : null,
          question_count: f.question_count ?? null,
          time_given: f.time_given ?? null,
          due_at: f.due_at ?? null,
          took: typeof f.took === 'number' ? f.took : null,
          assigned: typeof f.assigned === 'number' ? f.assigned : null,
          avg_score: typeof f.avg_score === 'number' ? f.avg_score : null,
        })),
      };
    }),

  // The exam-sets list (newest first). A 404 (older backend) is the caller's cue to hide the card.
  examSets: (limit = 20) =>
    API.get<{ exam_sets?: ExamSetListRow[] }>(`${BASE}/exam-sets/`, { params: { limit } }).then(r => r.data?.exam_sets ?? []),
};
